// @oasis-permission-inherits: /ai-edits (studio-edits:view)
import { skipToken } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { GridIcon, LoaderCircleIcon, TableIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

import { InfiniteQueryPagination } from "~/components/infinite-query-pagination";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  WorkflowFilterBar,
  type WorkflowFilters,
  countActiveFilters,
  matchesFilters,
} from "~/modules/cactus-generations/components/workflow-filter-bar";
import { WorkflowOutputCard } from "~/modules/cactus-generations/components/workflow-output-card";
import { WorkflowServerFilters } from "~/modules/cactus-generations/components/workflow-server-filters";
import { WorkflowTableView } from "~/modules/cactus-generations/components/workflow-table-view";
import {
  fetchWorkflowDefsQuery,
  fetchWorkflowOutputsQuery,
} from "~/modules/cactus-generations/queries";
import ResponsiveGrid from "~/modules/mirage/components/responsive-grid";

const viewModes = ["grid", "table"] as const;
type ViewMode = (typeof viewModes)[number];

export const Route = createFileRoute("/_dash/ai-edits/cactus-tools/")({
  validateSearch: z.object({
    workflow: z.string().optional(),
    page: z.number().optional(),
    view: z.enum(viewModes).optional(),
    columns: z.string().optional(),
    excludeOryxEmployees: z.boolean().optional(),
    excludeDevAccounts: z.boolean().optional(),
    linqPhoneNumber: z.string().optional(),
    wrap: z.boolean().optional(),
  }),
  component: RouteComponent,
});

function hasOutput(o: {
  jsonResult: Record<string, unknown> | null;
  assetUri: string | null;
  assetId: string | null;
}) {
  return !!o.assetUri || !!o.assetId || (!!o.jsonResult && Object.keys(o.jsonResult).length > 0);
}

function RouteComponent() {
  const {
    workflow: selectedWorkflow,
    page,
    view,
    columns,
    wrap,
    linqPhoneNumber,
    excludeOryxEmployees,
    excludeDevAccounts,
  } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const currentView: ViewMode = view ?? "grid";
  const selectedColumns = useMemo(() => {
    if (!columns) {
      return [];
    }
    return columns.split(",").filter(Boolean);
  }, [columns]);

  const [filters, setFilters] = useState<WorkflowFilters>({});
  const hasClientFilters = countActiveFilters(filters) > 0;

  const { data: workflowDefsData, isLoading: isLoadingWorkflowDefs } =
    fetchWorkflowDefsQuery.useQuery({});

  const workflowDefs = workflowDefsData?.workflowTypes ?? [];
  const currentWorkflow = workflowDefs.find((w) => w.id === selectedWorkflow) ?? workflowDefs[0];
  const isOryxWorkflow = currentWorkflow?.id === "oryx";
  const outputPageSize = isOryxWorkflow ? 12 : 20;
  const gridConfig = isOryxWorkflow
    ? { gap: 18, maxColumns: 4, itemMinWidth: 240, itemMinHeight: 420 }
    : { gap: 16, maxColumns: 5, itemMinWidth: 200, itemMinHeight: 200 };
  const effectiveLinqPhoneNumber = isOryxWorkflow ? linqPhoneNumber : undefined;
  const effectiveExcludeOryxEmployees = isOryxWorkflow ? (excludeOryxEmployees ?? false) : false;
  const effectiveExcludeDevAccounts = isOryxWorkflow ? (excludeDevAccounts ?? false) : false;

  const {
    data: outputsData,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingOutputs,
    isFetchingNextPage,
  } = fetchWorkflowOutputsQuery.useInfiniteQuery(
    currentWorkflow
      ? {
          pathParams: { workflowName: currentWorkflow.id },
          params: {
            pageSize: outputPageSize,
            excludeOryxEmployees: effectiveExcludeOryxEmployees || undefined,
            excludeDevAccounts: effectiveExcludeDevAccounts || undefined,
            linqPhoneNumber: effectiveLinqPhoneNumber,
          },
        }
      : skipToken
  );

  const totalPages = outputsData?.pages.length ?? 0;
  const [currentPageIndex, setCurrentPageIndex] = useState(Math.max(0, (page ?? 1) - 1));

  useEffect(() => {
    setCurrentPageIndex(Math.max(0, (page ?? 1) - 1));
  }, [
    page,
    currentWorkflow?.id,
    effectiveLinqPhoneNumber,
    effectiveExcludeOryxEmployees,
    effectiveExcludeDevAccounts,
  ]);

  useEffect(() => {
    if (
      !currentWorkflow ||
      isOryxWorkflow ||
      (!linqPhoneNumber && !excludeOryxEmployees && !excludeDevAccounts)
    ) {
      return;
    }

    navigate({
      replace: true,
      search: {
        workflow: currentWorkflow.id,
        page: currentPageIndex + 1,
        view: currentView,
        columns: columns || undefined,
        wrap: wrap || undefined,
      },
    });
  }, [
    columns,
    currentPageIndex,
    currentView,
    currentWorkflow,
    excludeDevAccounts,
    excludeOryxEmployees,
    isOryxWorkflow,
    linqPhoneNumber,
    navigate,
    wrap,
  ]);

  const currentPageData = outputsData?.pages[currentPageIndex];

  const allOutputs = useMemo(() => {
    if (!outputsData?.pages) {
      return [];
    }
    return outputsData.pages.flatMap((p) => p.outputs.page).filter(hasOutput);
  }, [outputsData]);

  const currentPageOutputs = useMemo(() => {
    return currentPageData?.outputs.page?.filter(hasOutput) ?? [];
  }, [currentPageData]);

  const displayedOutputs = useMemo(() => {
    const base = hasClientFilters ? allOutputs : currentPageOutputs;
    if (!hasClientFilters) {
      return base;
    }
    return base.filter((output) => matchesFilters(output, filters));
  }, [allOutputs, currentPageOutputs, hasClientFilters, filters]);

  const navigateWithSearch = (overrides: Record<string, unknown>) => {
    navigate({
      search: {
        workflow: currentWorkflow?.id,
        page: currentPageIndex + 1,
        view: currentView,
        columns: columns || undefined,
        excludeOryxEmployees: effectiveExcludeOryxEmployees || undefined,
        excludeDevAccounts: effectiveExcludeDevAccounts || undefined,
        linqPhoneNumber: effectiveLinqPhoneNumber,
        wrap: wrap || undefined,
        ...overrides,
      },
    });
  };

  const onPreviousPage = () => {
    const previousPageIndex = Math.max(0, currentPageIndex - 1);
    setCurrentPageIndex(previousPageIndex);
    navigateWithSearch({ page: previousPageIndex + 1 });
  };

  const onNextPage = async () => {
    const nextPageIndex = currentPageIndex + 1;
    setCurrentPageIndex(nextPageIndex);
    navigateWithSearch({ page: nextPageIndex + 1 });
    if (nextPageIndex === totalPages) {
      await fetchNextPage();
    }
  };

  const onPageChange = (pageIndex: number) => {
    setCurrentPageIndex(pageIndex);
    navigateWithSearch({ page: pageIndex + 1 });
  };

  const handleWorkflowChange = (workflowId: string) => {
    setCurrentPageIndex(0);
    setFilters({});
    const isSameWorkflow = workflowId === currentWorkflow?.id;
    navigate({
      search: {
        excludeOryxEmployees:
          workflowId === "oryx" ? effectiveExcludeOryxEmployees || undefined : undefined,
        excludeDevAccounts:
          workflowId === "oryx" ? effectiveExcludeDevAccounts || undefined : undefined,
        linqPhoneNumber: workflowId === "oryx" ? effectiveLinqPhoneNumber : undefined,
        workflow: workflowId,
        page: 1,
        ...(isSameWorkflow
          ? { view: currentView, columns: columns || undefined, wrap: wrap || undefined }
          : {}),
      },
    });
  };

  const handleViewChange = (newView: ViewMode) => {
    navigateWithSearch({ view: newView });
  };

  const handleColumnsChange = (newColumns: string[]) => {
    navigateWithSearch({
      columns: newColumns.length > 0 ? newColumns.join(",") : undefined,
    });
  };

  const handleWrapTextChange = (newWrap: boolean) => {
    navigateWithSearch({ wrap: newWrap || undefined });
  };

  const handleServerFiltersApply = ({
    linqPhoneNumber: nextLinqPhoneNumber,
    excludeOryxEmployees: nextExcludeOryxEmployees,
    excludeDevAccounts: nextExcludeDevAccounts,
  }: {
    excludeOryxEmployees: boolean;
    excludeDevAccounts: boolean;
    linqPhoneNumber?: string;
  }) => {
    setCurrentPageIndex(0);
    navigateWithSearch({
      excludeOryxEmployees: nextExcludeOryxEmployees || undefined,
      excludeDevAccounts: nextExcludeDevAccounts || undefined,
      linqPhoneNumber: nextLinqPhoneNumber || undefined,
      page: 1,
    });
  };

  if (isLoadingWorkflowDefs) {
    return (
      <div className="flex gap-6 h-full">
        <div className="w-64 shrink-0 space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
        <div className="flex-1">
          <Skeleton className="h-8 w-48 mb-6" />
          <ResponsiveGrid
            gap={gridConfig.gap}
            maxColumns={gridConfig.maxColumns}
            itemMinWidth={gridConfig.itemMinWidth}
            itemMinHeight={gridConfig.itemMinHeight}
          >
            {Array.from({ length: outputPageSize }).map((_, index) => (
              <Skeleton key={index} className="aspect-9/16 w-full h-full rounded-lg" />
            ))}
          </ResponsiveGrid>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-6 h-full">
      <div className="w-64 shrink-0 border-r pr-4">
        <h2 className="text-sm font-semibold text-muted-foreground mb-3">Workflows</h2>
        <div className="space-y-1">
          {workflowDefs.map((workflow) => (
            <Button
              key={workflow.id}
              variant={workflow.id === currentWorkflow?.id ? "secondary" : "ghost"}
              className="w-full justify-start text-left h-auto py-2 px-3"
              onClick={() => handleWorkflowChange(workflow.id)}
            >
              <span className="truncate text-sm">{workflow.name}</span>
            </Button>
          ))}
        </div>
      </div>

      <div className="flex-1 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold">{currentWorkflow?.name ?? "Cactus Tools"}</h1>
            <div className="flex items-center border rounded-md">
              <Button
                variant={currentView === "grid" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 w-7 p-0 rounded-r-none"
                onClick={() => handleViewChange("grid")}
              >
                <GridIcon className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant={currentView === "table" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 w-7 p-0 rounded-l-none"
                onClick={() => handleViewChange("table")}
              >
                <TableIcon className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          {!hasClientFilters && totalPages > 0 && (
            <InfiniteQueryPagination
              onPreviousPage={onPreviousPage}
              onNextPage={onNextPage}
              onPageChange={onPageChange}
              currentPageIndex={currentPageIndex}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              totalPages={totalPages}
            />
          )}
        </div>

        {isOryxWorkflow && (
          <WorkflowServerFilters
            excludeOryxEmployees={effectiveExcludeOryxEmployees}
            excludeDevAccounts={effectiveExcludeDevAccounts}
            linqPhoneNumber={effectiveLinqPhoneNumber}
            onApply={handleServerFiltersApply}
          />
        )}

        <WorkflowFilterBar allOutputs={allOutputs} filters={filters} onFiltersChange={setFilters} />

        {hasClientFilters && (
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span>
              {displayedOutputs.length} of {allOutputs.length} results match
              {totalPages > 0 && ` (across ${totalPages} loaded pages)`}
            </span>
            {hasNextPage && (
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-xs gap-1.5"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage && <LoaderCircleIcon className="h-3 w-3 animate-spin" />}
                Load more to search
              </Button>
            )}
          </div>
        )}

        {currentView === "grid" ? (
          <>
            <ResponsiveGrid
              gap={gridConfig.gap}
              maxColumns={gridConfig.maxColumns}
              itemMinWidth={gridConfig.itemMinWidth}
              itemMinHeight={gridConfig.itemMinHeight}
            >
              {(isLoadingOutputs || isFetchingNextPage) &&
                !hasClientFilters &&
                Array.from({ length: outputPageSize }).map((_, index) => (
                  <Skeleton key={index} className="aspect-9/16 w-full h-full rounded-lg" />
                ))}
              {displayedOutputs.map((output) => (
                <WorkflowOutputCard
                  key={output.workflowRunId}
                  output={output}
                  linkTo="/ai-edits/cactus-tools/$workflowIdentifier/$workflowRunId"
                  linkParams={{
                    workflowIdentifier: output.workflowName,
                    workflowRunId: output.workflowRunId,
                  }}
                  linkSearch={{
                    excludeOryxEmployees: effectiveExcludeOryxEmployees || undefined,
                    excludeDevAccounts: effectiveExcludeDevAccounts || undefined,
                    linqPhoneNumber: effectiveLinqPhoneNumber,
                    workflow: currentWorkflow?.id,
                    page: currentPageIndex + 1,
                    view: currentView,
                    columns: columns || undefined,
                    wrap: wrap || undefined,
                  }}
                />
              ))}
              {!isLoadingOutputs && !isFetchingNextPage && displayedOutputs.length === 0 && (
                <div className="flex text-sm text-muted-foreground col-span-full">
                  {hasClientFilters
                    ? "No results match your filters"
                    : "No outputs found for this workflow"}
                </div>
              )}
            </ResponsiveGrid>
          </>
        ) : (
          <>
            {(isLoadingOutputs || isFetchingNextPage) && !hasClientFilters && (
              <div className="space-y-2">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            )}
            {displayedOutputs.length > 0 && !isLoadingOutputs && (
              <WorkflowTableView
                excludeOryxEmployees={effectiveExcludeOryxEmployees}
                excludeDevAccounts={effectiveExcludeDevAccounts}
                linqPhoneNumber={effectiveLinqPhoneNumber}
                outputs={displayedOutputs}
                selectedColumns={selectedColumns}
                onColumnsChange={handleColumnsChange}
                currentWorkflowId={currentWorkflow?.id}
                currentPage={currentPageIndex + 1}
                wrapText={wrap ?? false}
                onWrapTextChange={handleWrapTextChange}
              />
            )}
            {!isLoadingOutputs && !isFetchingNextPage && displayedOutputs.length === 0 && (
              <div className="flex text-sm text-muted-foreground">
                {hasClientFilters
                  ? "No results match your filters"
                  : "No outputs found for this workflow"}
              </div>
            )}
          </>
        )}

        {!hasClientFilters && totalPages > 0 && !isLoadingOutputs && !isFetchingNextPage && (
          <div className="flex justify-end w-full mb-16">
            <InfiniteQueryPagination
              onPreviousPage={onPreviousPage}
              onNextPage={onNextPage}
              onPageChange={onPageChange}
              currentPageIndex={currentPageIndex}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              totalPages={totalPages}
            />
          </div>
        )}
      </div>
    </div>
  );
}
